import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertTriangle, FiCheck, FiChevronLeft } from "react-icons/fi";
import SimpleModal from "components/Modal/SimpleModal";
import "./PlanMagicPrerequisitesModal.scss";

const REQUIREMENT_ORDER = ["functions", "availability", "staffing"];

const employeeLabel = (employee) =>
	`${employee?.name || ""} ${employee?.surname || ""}`.trim();

const PlanMagicPrerequisitesModal = ({
	isOpen,
	missing = [],
	functionEmployees = [],
	unresolvedFunctionIds = [],
	onClose,
	onAssignFunctions,
	onConfigureAvailability,
	onDefineStaffing,
}) => {
	const { t } = useTranslation();
	const [functionsListOpen, setFunctionsListOpen] = useState(false);

	const remainingEmployees = functionEmployees.filter((employee) =>
		unresolvedFunctionIds.includes(employee.id)
	);
	const remainingEmployee = remainingEmployees[0];
	const unresolvedCount = remainingEmployees.length;
	const showFunctionList = unresolvedCount > 1;

	useEffect(() => {
		if (!isOpen || !missing.includes("functions") || !showFunctionList) {
			setFunctionsListOpen(false);
		}
	}, [isOpen, missing, showFunctionList]);

	if (!isOpen) return null;

	const items = REQUIREMENT_ORDER.filter((key) => missing.includes(key)).map(
		(key) => {
			if (key === "functions") {
				return {
					key,
					title: t("magicPlan.prerequisites.functionsTitle"),
					body: t("magicPlan.prerequisites.functionsBody", {
						count: unresolvedCount || 1,
						name: remainingEmployee
							? employeeLabel(remainingEmployee)
							: "",
					}),
					action: t("magicPlan.prerequisites.functionsAction"),
					onClick: () => {
						if (showFunctionList) {
							setFunctionsListOpen(true);
							return;
						}
						onAssignFunctions(remainingEmployee);
					},
				};
			}
			if (key === "availability") {
				return {
					key,
					title: t("magicPlan.prerequisites.availabilityTitle"),
					body: t("magicPlan.prerequisites.availabilityBody"),
					action: t("magicPlan.prerequisites.availabilityAction"),
					onClick: onConfigureAvailability,
				};
			}
			return {
				key,
				title: t("magicPlan.prerequisites.staffingTitle"),
				body: t("magicPlan.prerequisites.staffingBody"),
				action: t("magicPlan.prerequisites.staffingAction"),
				onClick: onDefineStaffing,
			};
		}
	);

	const renderAlertsView = () => (
		<>
			<div className="plan-magic-prereq__icon" aria-hidden="true">
				<FiAlertTriangle />
			</div>
			<h2 className="plan-magic-prereq__title">
				{t("magicPlan.prerequisites.title")}
			</h2>
			<p className="plan-magic-prereq__intro">
				{t("magicPlan.prerequisites.intro", { count: items.length })}
			</p>
			<ul className="plan-magic-prereq__list">
				{items.map((item) => (
					<li key={item.key} className="plan-magic-prereq__item">
						<p className="plan-magic-prereq__item-title">{item.title}</p>
						<p className="plan-magic-prereq__item-body">{item.body}</p>
						<button
							type="button"
							className="plan-magic-prereq__link"
							onClick={item.onClick}
						>
							{item.action} <span aria-hidden="true">→</span>
						</button>
					</li>
				))}
			</ul>
			<button
				type="button"
				className="btn btn-light plan-magic-prereq__dismiss"
				onClick={onClose}
			>
				{t("common.understood")}
			</button>
		</>
	);

	const renderFunctionsListView = () => (
		<>
			<button
				type="button"
				className="plan-magic-prereq__back"
				onClick={() => setFunctionsListOpen(false)}
			>
				<FiChevronLeft aria-hidden="true" />
				{t("common.back")}
			</button>
			<h2 className="plan-magic-prereq__title">
				{t("magicPlan.prerequisites.functionsAction")}
			</h2>
			<p className="plan-magic-prereq__intro">
				{t("magicPlan.prerequisites.functionsListIntro", {
					count: unresolvedCount || functionEmployees.length,
				})}
			</p>
			<ul className="plan-magic-prereq__employees plan-magic-prereq__employees--page">
				{functionEmployees.map((employee) => {
					const done = !unresolvedFunctionIds.includes(employee.id);
					return (
						<li key={employee.id}>
							<button
								type="button"
								className={`plan-magic-prereq__employee ${
									done ? "is-done" : ""
								}`}
								onClick={() => !done && onAssignFunctions(employee)}
								disabled={done}
							>
								<span
									className={`plan-magic-prereq__check ${
										done ? "is-done" : ""
									}`}
									aria-hidden="true"
								>
									{done ? <FiCheck /> : null}
								</span>
								<span className="plan-magic-prereq__employee-name">
									{employeeLabel(employee)}
								</span>
							</button>
						</li>
					);
				})}
			</ul>
		</>
	);

	return (
		<SimpleModal
			isOpen={isOpen}
			onClose={onClose}
			className="plan-magic-prereq-modal"
		>
			<div
				className={`plan-magic-prereq ${
					functionsListOpen && showFunctionList
						? "plan-magic-prereq--list"
						: ""
				}`}
			>
				{functionsListOpen && showFunctionList
					? renderFunctionsListView()
					: renderAlertsView()}
			</div>
		</SimpleModal>
	);
};

export default PlanMagicPrerequisitesModal;
